import { TrendingUp } from 'lucide-react';
import { MenuItem } from '../../../api/queries/menu.queries';

interface DrinkCardProps {
  drink: MenuItem;
  onClick: () => void;
  getLangValue: (multiLang: any) => string;
}

function DrinkCard({ drink, onClick, getLangValue }: DrinkCardProps) {
  // Format price for display
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <button
      onClick={onClick}
      disabled={!drink.isAvailable}
      className={`
        relative flex flex-col overflow-hidden rounded-lg border bg-card text-left
        transition-all duration-75 active:scale-95 touch-target tap-highlight-none
        ${drink.isAvailable
          ? 'hover:shadow-md hover:border-primary cursor-pointer'
          : 'opacity-50 cursor-not-allowed'
        }
      `}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-muted">
        {drink.imageUrl ? (
          <img
            src={drink.imageUrl}
            alt={getLangValue(drink.name)}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <span className="text-4xl">🥤</span>
          </div>
        )}

        {/* Trending badge */}
        {drink.isTrending && (
          <div className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 shadow-md">
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Unavailable overlay */}
        {!drink.isAvailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white font-medium">
            Unavailable
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1 p-3">
        {/* Name and Price row */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm line-clamp-2 flex-1">
            {getLangValue(drink.name)}
          </h3>
          <div className="flex items-baseline gap-1 shrink-0">
            <span className="text-sm font-bold text-primary">
              {formatPrice(drink.basePrice)}
            </span>
            {drink.sizePricing && Object.keys(drink.sizePricing).length > 0 && (
              <span className="text-xs text-muted-foreground">+</span>
            )}
          </div>
        </div>

        {/* Tags */}
        {drink.tags && drink.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {drink.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

export default DrinkCard;
